import { useEffect, useState } from 'react';
import { Download, Merge, Plus, Search, Star, Upload, User, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { callBackend, useMutation, usePaginatedQuery, useQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { CallButton } from './CallButton';
import { CsvImport } from './CsvImport';
import { downloadText, toCsv } from './csv';
import { buttonClass, errorMessage, formatWhen, initials, inlineInputClass, inputClass } from './format';
import { LIFECYCLE_LABELS, type CrmContact, type Lifecycle } from './types';

function MergeModal({ contacts, onClose }: { contacts: CrmContact[]; onClose: () => void }) {
  const mergeContacts = useMutation(backendApi.crm.mergeContacts);
  const [primaryId, setPrimaryId] = useState(contacts[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const merge = async () => {
    setBusy(true);
    try {
      await mergeContacts({ primaryId, otherIds: contacts.filter((contact) => contact.id !== primaryId).map((contact) => contact.id) });
      toast.success(`Merged ${contacts.length} contacts`);
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal isOpen onClose={onClose} title={`Merge ${contacts.length} contacts`}>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        Keep one. Its details stay; the others' numbers, calls, notes and tasks move onto it, and the others are deleted.
      </p>
      <div className="space-y-2 mb-6">
        {contacts.map((contact) => (
          <label key={contact.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${primaryId === contact.id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700'}`}>
            <input type="radio" name="primary" checked={primaryId === contact.id} onChange={() => setPrimaryId(contact.id)} />
            <span className="min-w-0">
              <span className="block font-medium text-gray-900 dark:text-white">{contact.name}</span>
              <span className="block text-xs text-gray-500">
                {[contact.company, contact.phones.map((phone) => phone.number).join(', '), `${contact.stats.totalInteractions} calls`].filter(Boolean).join(' · ')}
              </span>
            </span>
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className={buttonClass.secondary}>
          Cancel
        </button>
        <button type="button" onClick={() => void merge()} disabled={busy} className={buttonClass.primary}>
          <Merge className="w-4 h-4" /> Merge
        </button>
      </div>
    </Modal>
  );
}

export function ContactsTab() {
  const { data, openContact, openNewContact } = useCrm();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sort, setSort] = useState<'name' | 'recent'>('name');
  const [tag, setTag] = useState('');
  const [lifecycle, setLifecycle] = useState<Lifecycle | ''>('');
  const [vipOnly, setVipOnly] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [selected, setSelected] = useState<Map<string, CrmContact>>(new Map());
  const [merging, setMerging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const tags = useQuery<Array<{ tag: string; count: number }>>(backendApi.crm.listTags, {});

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const contacts = usePaginatedQuery<CrmContact>(
    backendApi.crm.listContacts,
    { search: debounced || undefined, sort, tag: tag || undefined, lifecycle: lifecycle || undefined, vipOnly: vipOnly || undefined },
    50
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      const result = await callBackend<{ customFields: Array<{ key: string; label: string }>; rows: Array<Record<string, any>> }>(backendApi.crm.exportContacts, {});
      const columns = ['name', 'company', 'jobTitle', 'email', 'mobile', 'office', 'other', 'tags', 'lifecycle', 'owner', 'vip', 'notes', 'totalCalls', 'missedCalls', 'lastCall'];
      const header = ['Name', 'Company', 'Job title', 'Email', 'Mobile', 'Office', 'Other numbers', 'Tags', 'Stage', 'Owner', 'VIP', 'Notes', 'Calls', 'Missed calls', 'Last call', ...result.customFields.map((field) => field.label)];
      const rows = result.rows.map((row) => [...columns.map((column) => row[column]), ...result.customFields.map((field) => row.customFields?.[field.key] ?? '')]);
      downloadText(`contacts-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(header, rows));
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setExporting(false);
    }
  };

  const toggle = (contact: CrmContact) =>
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(contact.id)) next.delete(contact.id);
      else next.set(contact.id, contact);
      return next;
    });

  return (
    <Card animate={false}>
      <div className="flex flex-col xl:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, company, email, number or tag"
            className={`${inputClass} pl-9`}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={sort} onChange={(event) => setSort(event.target.value as 'name' | 'recent')} className={`${inlineInputClass}`} aria-label="Sort" disabled={Boolean(debounced)}>
            <option value="name">A–Z</option>
            <option value="recent">Recent activity</option>
          </select>
          <select value={tag} onChange={(event) => setTag(event.target.value)} className={`${inlineInputClass}`} aria-label="Tag">
            <option value="">All tags</option>
            {(tags ?? []).map((row) => (
              <option key={row.tag} value={row.tag}>
                {row.tag} ({row.count})
              </option>
            ))}
          </select>
          <select value={lifecycle} onChange={(event) => setLifecycle(event.target.value as Lifecycle | '')} className={`${inlineInputClass}`} aria-label="Stage">
            <option value="">All stages</option>
            {Object.entries(LIFECYCLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => setVipOnly((value) => !value)} className={vipOnly ? buttonClass.primary : buttonClass.secondary} aria-pressed={vipOnly}>
            <Star className="w-4 h-4" /> VIP
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {data?.canManage && (
          <>
            <button type="button" onClick={() => openNewContact()} className={buttonClass.primary}>
              <Plus className="w-4 h-4" /> Contact
            </button>
            <button type="button" onClick={() => setImporting(true)} className={buttonClass.secondary}>
              <Upload className="w-4 h-4" /> Import CSV
            </button>
          </>
        )}
        <button type="button" onClick={() => void exportCsv()} disabled={exporting} className={buttonClass.secondary}>
          <Download className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
        {data?.canManage &&
          (mergeMode ? (
            <>
              <button type="button" onClick={() => setMerging(true)} disabled={selected.size < 2} className={buttonClass.primary}>
                <Merge className="w-4 h-4" /> Merge {selected.size}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMergeMode(false);
                  setSelected(new Map());
                }}
                className={buttonClass.secondary}
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setMergeMode(true)} className={buttonClass.secondary}>
              <Merge className="w-4 h-4" /> Merge
            </button>
          ))}
      </div>

      {contacts.error && <p className="text-red-600 text-sm">{contacts.error}</p>}
      {contacts.isLoading && <p className="text-gray-500 text-sm">Loading…</p>}
      {!contacts.isLoading && !contacts.error && contacts.isDone && contacts.items.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <User className="w-10 h-10 mx-auto mb-2 opacity-60" />
          {debounced || tag || lifecycle || vipOnly ? 'No contacts match.' : 'No contacts yet. Add one, import a CSV, or name callers from New callers.'}
        </div>
      )}

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {contacts.items.map((contact) => (
          <div key={contact.id} className="flex items-center gap-3 py-3">
            {mergeMode && (
              <input type="checkbox" checked={selected.has(contact.id)} onChange={() => toggle(contact)} className="w-4 h-4 rounded" aria-label={`Select ${contact.name}`} />
            )}
            <button type="button" onClick={() => (mergeMode ? toggle(contact) : openContact(contact.id))} className="flex items-center gap-3 min-w-0 flex-1 text-left">
              <div className="w-10 h-10 shrink-0 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 flex items-center justify-center text-sm font-semibold">
                {initials(contact.name)}
              </div>
              <div className="min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {contact.vip && <Star className="inline w-3.5 h-3.5 mr-1 -mt-0.5 text-amber-500 fill-amber-400" />}
                  {contact.name}
                  {contact.company && <span className="font-normal text-gray-500 dark:text-gray-400"> · {contact.company}</span>}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {[
                    contact.lifecycle ? LIFECYCLE_LABELS[contact.lifecycle] : null,
                    `${contact.stats.totalInteractions} calls`,
                    contact.stats.lastInteractionTimestamp ? `last ${formatWhen(contact.stats.lastInteractionTimestamp * 1000)}` : null,
                    contact.ownerName ?? contact.ownerExtension ? `owner ${contact.ownerName ?? contact.ownerExtension}` : null,
                    contact.tags.length ? contact.tags.join(', ') : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </button>
            {!mergeMode && contact.phones[0] && <CallButton number={contact.phones[0].number} size="sm" />}
          </div>
        ))}
      </div>
      {!contacts.isLoading && !contacts.error && !contacts.isDone && (
        <button type="button" onClick={contacts.loadMore} disabled={contacts.loadingMore} className={`${buttonClass.secondary} mt-4`}>
          {contacts.loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
      {merging && (
        <MergeModal
          contacts={Array.from(selected.values())}
          onClose={() => {
            setMerging(false);
            setMergeMode(false);
            setSelected(new Map());
          }}
        />
      )}
      {importing && <CsvImport onClose={() => setImporting(false)} />}
    </Card>
  );
}
